import React, { useState, useEffect, useCallback } from 'react';
import {
  getToolCategories,
  getGlobalPolicy,
  updateGlobalPolicy,
  getUserPolicy,
  updateUserPolicy,
  deleteUserPolicy,
  ToolCategoryInfo,
  PolicyDocument,
  CategoryPermission,
} from '../api';

const AdminSettings: React.FC = () => {
  const [categories, setCategories] = useState<ToolCategoryInfo[]>([]);
  const [policy, setPolicy] = useState<PolicyDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Domain management
  const [newWhiteDomain, setNewWhiteDomain] = useState('');
  const [newBlackDomain, setNewBlackDomain] = useState('');

  // User override
  const [userSearchEmail, setUserSearchEmail] = useState('');
  const [userPolicyId, setUserPolicyId] = useState('');
  const [userPolicy, setUserPolicyState] = useState<PolicyDocument | null>(null);
  const [userLoading, setUserLoading] = useState(false);
  const [userSaving, setUserSaving] = useState(false);
  const [userError, setUserError] = useState('');
  const [userSuccess, setUserSuccess] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [cats, pol] = await Promise.all([getToolCategories(), getGlobalPolicy()]);
      setCategories(cats);
      setPolicy(pol);
    } catch (err: any) {
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCategoryToggle = (catId: string, field: keyof CategoryPermission) => {
    if (!policy) return;
    const current = policy.categories[catId] || { enabled: true, allowRead: true, allowWrite: false };
    const updated = { ...current, [field]: !current[field] };
    // If disabling read, also disable write
    if (field === 'allowRead' && !updated.allowRead) updated.allowWrite = false;
    // If enabling write, also enable read
    if (field === 'allowWrite' && updated.allowWrite) updated.allowRead = true;
    // If disabling category, reset permissions
    if (field === 'enabled' && !updated.enabled) { updated.allowRead = false; updated.allowWrite = false; }
    if (field === 'enabled' && updated.enabled) { updated.allowRead = true; }
    setPolicy({ ...policy, categories: { ...policy.categories, [catId]: updated } });
  };

  const handleSaveGlobal = async () => {
    if (!policy) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const result = await updateGlobalPolicy({
        defaultPermission: policy.defaultPermission,
        categories: policy.categories,
        domainWhitelist: policy.domainWhitelist,
        domainBlacklist: policy.domainBlacklist,
      });
      setPolicy(result.policy);
      setSuccess('Global policy saved. Changes take effect within 5 minutes.');
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addDomain = (list: 'whitelist' | 'blacklist') => {
    if (!policy) return;
    const domain = list === 'whitelist' ? newWhiteDomain.trim() : newBlackDomain.trim();
    if (!domain || !/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain)) {
      setError('Enter a valid domain (e.g., dev.atlassian.net)');
      return;
    }
    if (list === 'whitelist') {
      if (policy.domainWhitelist.includes(domain)) return;
      setPolicy({ ...policy, domainWhitelist: [...policy.domainWhitelist, domain] });
      setNewWhiteDomain('');
    } else {
      if (policy.domainBlacklist.includes(domain)) return;
      setPolicy({ ...policy, domainBlacklist: [...policy.domainBlacklist, domain] });
      setNewBlackDomain('');
    }
  };

  const removeDomain = (list: 'whitelist' | 'blacklist', domain: string) => {
    if (!policy) return;
    if (list === 'whitelist') {
      setPolicy({ ...policy, domainWhitelist: policy.domainWhitelist.filter(d => d !== domain) });
    } else {
      setPolicy({ ...policy, domainBlacklist: policy.domainBlacklist.filter(d => d !== domain) });
    }
  };

  // User override handlers
  const handleSearchUser = async () => {
    const email = userSearchEmail.trim();
    if (!email) return;
    setUserLoading(true);
    setUserError('');
    setUserSuccess('');
    setUserPolicyState(null);
    setUserPolicyId('');
    try {
      // userId is email-based for now (portal sub). Admin enters user email.
      // For simplicity, use the email as a search hint — the admin needs to provide the userId.
      // We'll search by email as the policyId.
      const up = await getUserPolicy(email);
      setUserPolicyId(email);
      setUserPolicyState(up);
    } catch (err: any) {
      setUserError(err.message || 'Failed to load user policy');
    } finally {
      setUserLoading(false);
    }
  };

  const handleUserCategoryToggle = (catId: string, field: keyof CategoryPermission) => {
    if (!policy) return;
    const base = policy.categories[catId] || { enabled: true, allowRead: true, allowWrite: false };
    const currentUser = userPolicy?.categories?.[catId] || { ...base };
    const updated = { ...currentUser, [field]: !currentUser[field] };
    if (field === 'allowRead' && !updated.allowRead) updated.allowWrite = false;
    if (field === 'allowWrite' && updated.allowWrite) updated.allowRead = true;
    if (field === 'enabled' && !updated.enabled) { updated.allowRead = false; updated.allowWrite = false; }
    if (field === 'enabled' && updated.enabled) { updated.allowRead = true; }
    const cats = { ...(userPolicy?.categories || {}), [catId]: updated };
    setUserPolicyState(userPolicy ? { ...userPolicy, categories: cats } : {
      policyId: userPolicyId,
      version: 1,
      defaultPermission: 'read-only',
      categories: cats,
      domainWhitelist: [],
      domainBlacklist: [],
      updatedAt: '',
      updatedBy: '',
    });
  };

  const handleSaveUserPolicy = async () => {
    if (!userPolicyId) return;
    setUserSaving(true);
    setUserError('');
    setUserSuccess('');
    try {
      await updateUserPolicy(userPolicyId, {
        categories: userPolicy?.categories || {},
      });
      setUserSuccess('User policy override saved.');
    } catch (err: any) {
      setUserError(err.message || 'Failed to save');
    } finally {
      setUserSaving(false);
    }
  };

  const handleResetUserPolicy = async () => {
    if (!userPolicyId || !confirm('Remove all overrides for this user? They will use the global policy.')) return;
    setUserSaving(true);
    setUserError('');
    try {
      await deleteUserPolicy(userPolicyId);
      setUserPolicyState(null);
      setUserSuccess('User override removed. Global policy will apply.');
    } catch (err: any) {
      setUserError(err.message || 'Failed to reset');
    } finally {
      setUserSaving(false);
    }
  };

  if (loading) return <div style={s.container}><p>Loading settings...</p></div>;

  return (
    <div style={s.container}>
      {error && <div style={s.errorBox}>{error}</div>}
      {success && <div style={s.successBox}>{success}</div>}

      {/* ── Section 1: Global Tool Categories ── */}
      <div style={s.section}>
        <h2 style={s.sectionTitle}>Global Tool Category Permissions</h2>
        <p style={s.hint}>
          Control which tool categories are available and whether users can perform read-only or read-write operations.
          Changes apply to all users (unless overridden per-user below).
        </p>

        <div style={s.defaultRow}>
          <span style={s.label}>Default permission level:</span>
          <select
            value={policy?.defaultPermission || 'read-only'}
            onChange={e => policy && setPolicy({ ...policy, defaultPermission: e.target.value as any })}
            style={s.select}
          >
            <option value="read-only">Read-only</option>
            <option value="read-write">Read-write</option>
          </select>
        </div>

        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Category</th>
                <th style={s.thCenter}>Enabled</th>
                <th style={s.thCenter}>Read</th>
                <th style={s.thCenter}>Write</th>
                <th style={s.thRight}>Tools</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(cat => {
                const perm = policy?.categories[cat.id] || { enabled: true, allowRead: true, allowWrite: false };
                return (
                  <tr key={cat.id} style={s.tr}>
                    <td style={s.td}>{cat.displayName}</td>
                    <td style={s.tdCenter}>
                      <input type="checkbox" checked={perm.enabled} onChange={() => handleCategoryToggle(cat.id, 'enabled')} />
                    </td>
                    <td style={s.tdCenter}>
                      <input type="checkbox" checked={perm.allowRead} disabled={!perm.enabled} onChange={() => handleCategoryToggle(cat.id, 'allowRead')} />
                    </td>
                    <td style={s.tdCenter}>
                      <input type="checkbox" checked={perm.allowWrite} disabled={!perm.enabled} onChange={() => handleCategoryToggle(cat.id, 'allowWrite')} />
                    </td>
                    <td style={s.tdRight}>
                      <span style={s.countBadge}>{cat.readCount}R</span>
                      <span style={{ ...s.countBadge, ...s.writeBadge }}>{cat.writeCount}W</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Section 2: Domain Management ── */}
      <div style={s.section}>
        <h2 style={s.sectionTitle}>Atlassian Domain Controls</h2>
        <p style={s.hint}>
          Control which Atlassian instances MCP can connect to. Blacklisted domains are always blocked.
          If the whitelist is non-empty, only whitelisted domains are allowed.
        </p>

        <div style={s.domainRow}>
          <div style={s.domainCol}>
            <h3 style={s.domainLabel}>Allowed Domains (Whitelist)</h3>
            <p style={s.domainHint}>If empty, all domains are allowed (except blacklisted).</p>
            {(policy?.domainWhitelist || []).map(d => (
              <div key={d} style={s.domainItem}>
                <span style={s.domainText}>{d}</span>
                <button onClick={() => removeDomain('whitelist', d)} style={s.removeBtn}>Remove</button>
              </div>
            ))}
            <div style={s.addDomainRow}>
              <input
                type="text"
                value={newWhiteDomain}
                onChange={e => setNewWhiteDomain(e.target.value)}
                placeholder="dev.atlassian.net"
                style={s.domainInput}
                onKeyDown={e => e.key === 'Enter' && addDomain('whitelist')}
              />
              <button onClick={() => addDomain('whitelist')} style={s.addBtn}>Add</button>
            </div>
          </div>

          <div style={s.domainCol}>
            <h3 style={{ ...s.domainLabel, color: '#dc3545' }}>Blocked Domains (Blacklist)</h3>
            <p style={s.domainHint}>These domains are always blocked, even if whitelisted.</p>
            {(policy?.domainBlacklist || []).map(d => (
              <div key={d} style={s.domainItem}>
                <span style={{ ...s.domainText, color: '#dc3545' }}>{d}</span>
                <button onClick={() => removeDomain('blacklist', d)} style={s.removeBtn}>Remove</button>
              </div>
            ))}
            <div style={s.addDomainRow}>
              <input
                type="text"
                value={newBlackDomain}
                onChange={e => setNewBlackDomain(e.target.value)}
                placeholder="prod.atlassian.net"
                style={s.domainInput}
                onKeyDown={e => e.key === 'Enter' && addDomain('blacklist')}
              />
              <button onClick={() => addDomain('blacklist')} style={{ ...s.addBtn, background: '#dc3545' }}>Add</button>
            </div>
          </div>
        </div>
      </div>

      {/* Save button for global settings */}
      <div style={s.saveRow}>
        <button onClick={handleSaveGlobal} disabled={saving} style={s.saveBtn}>
          {saving ? 'Saving...' : 'Save Global Policy'}
        </button>
      </div>

      {/* ── Section 3: Per-User Overrides ── */}
      <div style={s.section}>
        <h2 style={s.sectionTitle}>Per-User Permission Overrides</h2>
        <p style={s.hint}>
          Override global category permissions for a specific user.
          Enter the user's ID (Cognito sub or email used during credential setup).
        </p>

        {userError && <div style={s.errorBox}>{userError}</div>}
        {userSuccess && <div style={s.successBox}>{userSuccess}</div>}

        <div style={s.searchRow}>
          <input
            type="text"
            value={userSearchEmail}
            onChange={e => setUserSearchEmail(e.target.value)}
            placeholder="User email or ID"
            style={s.searchInput}
            onKeyDown={e => e.key === 'Enter' && handleSearchUser()}
          />
          <button onClick={handleSearchUser} disabled={userLoading} style={s.searchBtn}>
            {userLoading ? 'Searching...' : 'Load User'}
          </button>
        </div>

        {userPolicyId && (
          <div style={s.userPolicyBox}>
            <p style={s.userPolicyLabel}>
              User: <strong>{userPolicyId}</strong>
              {userPolicy ? ' (has custom overrides)' : ' (using global defaults)'}
            </p>

            <div style={s.tableWrapper}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Category</th>
                    <th style={s.thCenter}>Enabled</th>
                    <th style={s.thCenter}>Read</th>
                    <th style={s.thCenter}>Write</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map(cat => {
                    const globalPerm = policy?.categories[cat.id] || { enabled: true, allowRead: true, allowWrite: false };
                    const userPerm = userPolicy?.categories?.[cat.id] || globalPerm;
                    const isOverridden = !!userPolicy?.categories?.[cat.id];
                    return (
                      <tr key={cat.id} style={{ ...s.tr, ...(isOverridden ? s.overriddenRow : {}) }}>
                        <td style={s.td}>
                          {cat.displayName}
                          {isOverridden && <span style={s.overrideBadge}>override</span>}
                        </td>
                        <td style={s.tdCenter}>
                          <input type="checkbox" checked={userPerm.enabled} onChange={() => handleUserCategoryToggle(cat.id, 'enabled')} />
                        </td>
                        <td style={s.tdCenter}>
                          <input type="checkbox" checked={userPerm.allowRead} disabled={!userPerm.enabled} onChange={() => handleUserCategoryToggle(cat.id, 'allowRead')} />
                        </td>
                        <td style={s.tdCenter}>
                          <input type="checkbox" checked={userPerm.allowWrite} disabled={!userPerm.enabled} onChange={() => handleUserCategoryToggle(cat.id, 'allowWrite')} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={s.userBtnRow}>
              <button onClick={handleSaveUserPolicy} disabled={userSaving} style={s.saveBtn}>
                {userSaving ? 'Saving...' : 'Save User Override'}
              </button>
              {userPolicy && (
                <button onClick={handleResetUserPolicy} disabled={userSaving} style={s.resetBtn}>
                  Reset to Global
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  container: { maxWidth: 900, margin: '0 auto' },
  section: { background: '#fff', borderRadius: 8, padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  sectionTitle: { fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.5rem', color: '#1a1a2e' },
  hint: { color: '#666', fontSize: '0.85rem', marginBottom: '1rem' },
  errorBox: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', padding: '0.75rem', borderRadius: 6, marginBottom: '1rem', fontSize: '0.9rem' },
  successBox: { background: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a', padding: '0.75rem', borderRadius: 6, marginBottom: '1rem', fontSize: '0.9rem' },
  label: { fontWeight: 600, fontSize: '0.9rem' },
  defaultRow: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' },
  select: { padding: '0.4rem 0.75rem', borderRadius: 4, border: '1px solid #d1d5db', fontSize: '0.9rem' },
  tableWrapper: { overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.9rem' },
  th: { textAlign: 'left' as const, padding: '0.5rem 0.75rem', borderBottom: '2px solid #e5e7eb', fontWeight: 600, color: '#374151' },
  thCenter: { textAlign: 'center' as const, padding: '0.5rem 0.75rem', borderBottom: '2px solid #e5e7eb', fontWeight: 600, color: '#374151' },
  thRight: { textAlign: 'right' as const, padding: '0.5rem 0.75rem', borderBottom: '2px solid #e5e7eb', fontWeight: 600, color: '#374151' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '0.5rem 0.75rem' },
  tdCenter: { padding: '0.5rem 0.75rem', textAlign: 'center' as const },
  tdRight: { padding: '0.5rem 0.75rem', textAlign: 'right' as const },
  countBadge: { display: 'inline-block', padding: '2px 6px', borderRadius: 4, background: '#e0f2fe', color: '#0369a1', fontSize: '0.75rem', fontWeight: 600, marginLeft: 4 },
  writeBadge: { background: '#fef3c7', color: '#92400e' },
  domainRow: { display: 'flex', gap: '2rem', flexWrap: 'wrap' as const },
  domainCol: { flex: 1, minWidth: 280 },
  domainLabel: { fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.25rem' },
  domainHint: { color: '#888', fontSize: '0.8rem', marginBottom: '0.5rem' },
  domainItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.4rem 0.6rem', background: '#f9fafb', borderRadius: 4, marginBottom: 4 },
  domainText: { fontFamily: 'monospace', fontSize: '0.85rem' },
  removeBtn: { background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 },
  addDomainRow: { display: 'flex', gap: '0.5rem', marginTop: '0.5rem' },
  domainInput: { flex: 1, padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem' },
  addBtn: { padding: '0.4rem 0.75rem', background: '#4a6cf7', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' },
  saveRow: { textAlign: 'center' as const, marginBottom: '1.5rem' },
  saveBtn: { padding: '0.6rem 1.5rem', background: '#4a6cf7', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem' },
  resetBtn: { padding: '0.6rem 1.5rem', background: '#6b7280', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem', marginLeft: '0.75rem' },
  searchRow: { display: 'flex', gap: '0.5rem', marginBottom: '1rem' },
  searchInput: { flex: 1, padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.9rem' },
  searchBtn: { padding: '0.5rem 1rem', background: '#4a6cf7', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 },
  userPolicyBox: { background: '#f9fafb', borderRadius: 8, padding: '1rem', marginTop: '0.5rem' },
  userPolicyLabel: { fontSize: '0.9rem', marginBottom: '0.75rem' },
  userBtnRow: { marginTop: '1rem', display: 'flex', gap: '0.5rem' },
  overriddenRow: { background: '#fffbeb' },
  overrideBadge: { display: 'inline-block', marginLeft: 6, padding: '1px 6px', borderRadius: 3, background: '#fbbf24', color: '#78350f', fontSize: '0.7rem', fontWeight: 600 },
};

export default AdminSettings;
